import { DragAndDropId } from '@dealstack/frontend/app/pages/Project/Cap/CapEntityManageFlow/tabs/Securities/SecurityRanking/securityRankingTypes.ts';
import {
	ClientRect,
	Collision,
	CollisionDetection,
	UniqueIdentifier,
	pointerWithin,
} from '@dnd-kit/core';

type CollisionArgs = Parameters<CollisionDetection>[0];

/** Area of the overlap between two rects; 0 when they do not overlap. */
const overlapArea = (a: ClientRect, b: ClientRect): number =>
	Math.max(0, Math.min(a.right, b.right) - Math.max(a.left, b.left)) *
	Math.max(0, Math.min(a.bottom, b.bottom) - Math.max(a.top, b.top));

/**
 * Drop zones the dragged card overlaps, most-covered first.
 *
 * Deliberately not `rectIntersection`: that scores by intersection-over-union, which is the very
 * bias this module exists to avoid. Raw overlap area is size-neutral, so a tier cannot lose to a
 * smaller neighbour it barely touches.
 */
const byOverlapArea = (
	args: CollisionArgs,
	isCandidate: (id: UniqueIdentifier) => boolean
): Collision[] => {
	const { droppableContainers, droppableRects, collisionRect } = args;
	const scored: { collision: Collision; area: number }[] = [];

	for (const droppableContainer of droppableContainers) {
		const { id } = droppableContainer;

		if (!isCandidate(id)) {
			continue;
		}

		const rect = droppableRects.get(id);
		const area = rect ? overlapArea(rect, collisionRect) : 0;

		if (area > 0) {
			scored.push({ collision: { id, data: { droppableContainer, value: area } }, area });
		}
	}

	return scored.sort((a, b) => b.area - a.area).map(({ collision }) => collision);
};

const isRankingContent = (id: UniqueIdentifier) => id === DragAndDropId.rankingContent;
const isSidebar = (id: UniqueIdentifier) => id === DragAndDropId.sidebar;

/**
 * Resolves which drop zone a dragged security card belongs to.
 *
 * dnd-kit's `rectIntersection` scores droppables by intersection-over-*union*, so a tier's score
 * shrinks as it fills up: a 20-security tier peaks around 0.03, low enough that a neighbouring
 * one-security tier steals the drop on ~13% of card overlap. Dropping well inside a busy tier
 * therefore landed the card in the tier next to it (WAT-745). No path here may rank two tiers
 * against each other by that metric.
 *
 * The cursor is the honest signal, so `pointerWithin` decides where it can. Two adjustments to its
 * output, both because dnd-kit measures unclipped rects while the ranking column is `overflow:
 * auto`:
 *
 * - The ranking content wraps every tier, so it collides whenever a tier does. It is a fallback,
 *   not a competitor: its rect is the scroll viewport and a full tier can be taller than that, so
 *   ranking against it would let the container outrank the tier the cursor is in.
 * - A tier scrolled out of view still contains a cursor parked above the column (over the page
 *   header), so a tier is trusted only while the cursor is inside the column too. The sidebar sits
 *   outside the column and is exempt.
 *
 * Everywhere the cursor is inside no drop zone - the column's 1rem flex gap, the sidebar's title
 * strip - overlap area decides, keeping the near-miss forgiveness the page had before.
 */
export const securityRankingCollisionDetection: CollisionDetection = (args) => {
	const pointerCollisions = pointerWithin(args);
	const container = pointerCollisions.find((collision) => isRankingContent(collision.id));
	const dropZones = pointerCollisions.filter(
		(collision) => !isRankingContent(collision.id) && (!!container || isSidebar(collision.id))
	);

	if (dropZones.length > 0) {
		// The sticky "new rank" placeholder floats over whatever tier is scrolled beneath it, so
		// when both contain the cursor the visually-topmost one wins rather than whichever rect
		// happens to have nearer corners.
		const placeholder = dropZones.find(
			(collision) => collision.id === DragAndDropId.placeholderRank
		);

		return placeholder
			? [placeholder, ...dropZones.filter((collision) => collision !== placeholder)]
			: dropZones;
	}

	if (container) {
		// Cursor is in the column but between tiers. Only tiers may win here - the sidebar means
		// "unrank" and must not claim a card the cursor never left the column for - and the
		// container stands in when no tier is overlapped at all, which is the "drop in open space
		// to create a rank" case.
		const tiers = byOverlapArea(args, (id) => !isRankingContent(id) && !isSidebar(id));

		return tiers.length > 0 ? tiers : [container];
	}

	// Cursor is outside every drop zone, so there is no region to scope to: let any of them win on
	// overlap, the sidebar included, so a card can still be unranked from the sidebar's padding.
	return byOverlapArea(args, (id) => !isRankingContent(id));
};
